import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from "lodash";
import { alertNotification } from "../../../../actions/alertNotification";
import { resetLinkTaskForNewTask } from "../../../../actions/linkedTasks";
import { resetRecurrence, resetSidebarReducer, setIsTaskSaved, setShowTaskSidebar } from "../../../../actions/taskSidebar";
import { kendo, label, notifyIcon } from "../../../../config";
import { useTaskVariableUpdates } from "../../../../editor/editorPopup/TaskCustomVariables/hooks/taskCustomVariable.hooks";
import { appendFieldsFromObject, createFormData } from "../../../../helper/formDataHelper";
import { addWorkflowElement, addWorkflowStages } from "../../../../shared/services/workflow.services";
import { validateInputLength } from "../../../../shared/validators/validator";
import { trackAddComment, trackAddTask } from "../../../../utils/analytics";
import { getNotification } from "../../../../utils/common";
import {
  addLinkedAndSubtasks,
  addNewComments,
  addTask,
  addTaskPanelVisibiltyOptions,
  getTasks,
  newTaskAttachments,
} from "../../../Tasks/tasks.service";
import { addNewCommentFiles } from "../../TaskSidebarContent/Comments/comments.service";
import { getCurrentProject } from "../../sidebar.helper";
import { useMemo } from "react";
import { addTaskNodes } from "../../../Workflow/RuleBasedFlow/ruleBasedFlow.service";

export const useSaveTask = () => {
  const dispatch = useDispatch();
  const { subTask, linkTask } = useSelector((state) => state.linkedTasks);
  const { user } = useSelector((state) => state.auth);
  const { positionId } = useSelector((state) => state.userPosition);
  const { allAttachments } = useSelector((state) => state.attachments);
  const { allComments } = useSelector((state) => state.comments);
  const { workflowStages, workflowBasicDetails } = useSelector((state) => state.taskWorkflowDetails);
  const { cloneTask } = useSelector((state) => state.cloneTaskDetails);
  const { task } = useSelector((state) => state.taskSidebar);

  const isRuleBasedFlow = useMemo(() => workflowBasicDetails?.IsRuleBasedFlow, [workflowBasicDetails]);

  const {
    newTaskData,
    taskUrl,
    isDependent,
    flowElements,
    showAttachments,
    showDescription,
    showLinkedTasksSubTasks,
    showComments,
    isNewTask,
    showSimplifiedDetail,
    showDescPopup,
    showEmail,
    tempYdocRoom,
  } = useSelector((state) => state.taskSidebar);
  const { addTaskCustomVariables } = useTaskVariableUpdates();

  const saveFlowElements = (taskId) => {
    if (!taskId) return;
    dispatch(addWorkflowElement({ workflowElements: flowElements, taskId, isNewTask }));
  };
 
  const saveTaskNodes = (taskId) => {
    dispatch(addTaskNodes({  taskId, workflowId: workflowBasicDetails?.Id }));
  };

  const saveTaskPanelVisibiltyOptions = (taskId) => {
    if (!taskId) return;
    addTaskPanelVisibiltyOptions({
      taskId: taskId,
      showDescription,
      showAttachments,
      showChildSubTasks: showLinkedTasksSubTasks,
      showComments,
      showEmail,
      showSimplifiedDetail,
      showTaskDescPopup: showDescPopup.status,
    });
  };

  const saveWorkflowStages = (taskId) => {
    if (!workflowStages.length || !taskId) return;
    dispatch(addWorkflowStages({ taskId, stages: workflowStages }));
  };

  async function getBlobFromURL(blobURL) {
    const response = await fetch(blobURL);
    const blob = await response.blob();
    return blob;
  }

  function blobToFile(blob, fileName) {
    // Create a File from the Blob
    const file = new File([blob], fileName, {
      type: blob.type,
      lastModified: Date.now(),
    });
    return file;
  }

  async function convertBlobURLToFile(blobURL, fileName) {
    try {
      // Fetch the Blob from the Blob URL
      const blob = await getBlobFromURL(blobURL);

      // Convert the Blob to a File
      const file = blobToFile(blob, fileName);

      return file;
    } catch (error) {
      console.error("Error converting Blob URL to File:", error);
    }
  }

  const getCommentsArrays = (comments) => {
    const result = comments.reduce(
      (acc, { Id, CommentImages, NewCommentImages, ...rest }, index) => {
        const uniqueId = `unique-${index + 1}`;

        // Add object to the first array without commentImages and with uniqueId
        acc.commentsToBeSaved.push({ ...rest, uniqueId });

        // Split commentImages by comma and add each as a separate object to the second array
        NewCommentImages?.forEach(async (image) => {
          const file = await convertBlobURLToFile(image.imageUrl, image.fileName);
          await acc.commentFiles.push(Object.assign(file, { uniqueId }));
        });

        return acc;
      },
      { commentsToBeSaved: [], commentImages: [], commentFiles: [] }
    );

    return result;
  };

  const saveTaskComments = async (taskId) => {
    if (!allComments.length || !taskId) return;
    const { commentsToBeSaved, commentFiles } = getCommentsArrays(allComments);
    const addedComments = await dispatch(addNewComments({ taskId: taskId, comments: commentsToBeSaved }));
    if (addedComments?.length > 0) {
      trackAddComment({ task_id: taskId, comment_count: addedComments.length });
      const { filesToBeSaved } = addedComments?.reduce(
        (acc, comment) => {
          acc.filesToBeSaved.push({ CommentId: comment.CommentId, files: commentFiles?.filter((file) => file.uniqueId === comment.UniqueId) });
          return acc;
        },
        { filesToBeSaved: [] }
      );

      filesToBeSaved?.forEach(({ CommentId, files }) => {
        if (files.length) {
          const fileSavePayload = { commentId: CommentId, files };
          const formData = createFormData();
          appendFieldsFromObject(formData, fileSavePayload);
          dispatch(addNewCommentFiles(formData));
        }
      });
    }
  };

  const saveNewTaskAttachments = async (taskId) => {
    if (!allAttachments?.length || !taskId) return;
    const attachments = [...allAttachments];
    const attachmentsToBeSave = [];
    const copyAttachments = [];
    attachments.forEach((attachment) => (attachment?.IsCopyAttachment ? copyAttachments.push(attachment) : attachmentsToBeSave.push(attachment)));
    let fileAttachments = [];
    fileAttachments = await Promise.all(
      attachmentsToBeSave.map((attachment) => {
        const { AttachmentLink, AttachmentName } = attachment;
        return convertBlobURLToFile(AttachmentLink, AttachmentName);
      })
    );

    const payload = { entityName: "Tasks", entityId: taskId, files: fileAttachments, copyAttachments: JSON.stringify(copyAttachments) };
    const formData = createFormData();
    appendFieldsFromObject(formData, payload);
    dispatch(newTaskAttachments(formData));
  };

  const getSafeDescription = (description) => (description?.trim() ? description : "<p> </p>");

  const saveTaskHandler = async () => {
    if (!isEmpty(newTaskData.name?.trim()) && validateInputLength(newTaskData.name?.trim(), kendo.INPUT_MIN_LENGTH, kendo.TASK_INPUT_LENGTH)) {
      const payload = {
        ...newTaskData,
        description: getSafeDescription(newTaskData?.description),
        project: newTaskData?.initialProjectChangedFromText ? newTaskData?.initialProjectId : newTaskData?.project,
        ...{
          companyId: user.companyId,
          currentProject: getCurrentProject(true),
          creator: user?.id,
          cloneTaskId: cloneTask?.taskId,
          taskUrl,
          isDependent,
          flowElementsCount: flowElements?.length,
          positionId,
          tempYdocRoom,
          isRuleBasedTask: workflowBasicDetails?.IsRuleBasedFlow,
        },
      };
      let response = await addTask(payload);
      if (response?.EntityId) {
        isNewTask &&
          (subTask.length || linkTask?.childTask?.length || linkTask?.parentTask?.length || linkTask?.relatedTo?.length) &&
          handleLinkedTasks(response.EntityId);
        isRuleBasedFlow ? saveTaskNodes(response.EntityId) : saveFlowElements(response.EntityId);
        saveTaskPanelVisibiltyOptions(response.EntityId);
        saveWorkflowStages(response.EntityId);
        await saveTaskComments(response.EntityId);
        saveNewTaskAttachments(response.EntityId);
        dispatch(setIsTaskSaved(true));
        dispatch(setShowTaskSidebar(false));
        dispatch(resetRecurrence());
        dispatch(resetSidebarReducer());
        addTaskCustomVariables([...newTaskData?.taskCustomVariables], response.EntityId);
        dispatch(resetLinkTaskForNewTask());
        getTasks(null, true);
        const taskPayload = {
          taskHistory: response.taskHistoryId,
          taskId: response.EntityId,
        };
        window.dispatchEvent(new CustomEvent('taskSidebarClosed'));
        trackAddTask({ task_id: response.EntityId, project_id: newTaskData?.project, project_task_code: response.projectTaskId });
        dispatch(alertNotification(true, `${response.projectTaskId} ${label.TASK_CREATED}`, notifyIcon.SUCCESS_ICON, taskPayload));
      } else {
        getNotification(label.ERROR_OCCURED, notifyIcon.ERROR_ICON);
      }
    }
  };

  const handleLinkedTasks = (taskId) => {
    let linkTasks = [];
    let subTasks = [];
    linkTasks = linkTasks.concat(linkTask?.childTask);
    linkTasks = linkTasks.concat(linkTask?.parentTask);
    linkTasks = linkTasks.concat(linkTask?.relatedTo);
    subTasks = subTask;
    subTasks = subTasks.map((task) => ({
      subtaskName: task?.Name,
      dueDate: task?.DueDate,
      projectId: newTaskData?.project,
      isCompleted: task?.IsCompleted,
    }));
    linkTasks = linkTasks.map((task) => ({
      workflowId: task?.WorkflowId,
      relationId: task?.RelationId,
      childTaskId: task?.ChildTaskId,
      linkedTaskId: task?.LinkTaskId,
    }));
    let payload = {
      taskId: taskId,
      linkTasks: JSON.stringify(linkTasks),
      subTasks: JSON.stringify(subTasks),
    };
    payload && dispatch(addLinkedAndSubtasks(payload));
  };

  return { saveTaskHandler, convertBlobURLToFile };
};
